import React, { useMemo, useState } from 'react';
import { Modal, message } from 'antd';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import { deleteEhrConnection } from 'api/ehr';
import useEhrConnections from 'hooks/useEhrConnections';
import useEhrOptions from 'hooks/useEhrOptions';
import EhrBadge from './EhrBadge';
import EhrConnectFlow from './EhrConnectFlow';
import { CustomEhrForm } from './CustomEhr';
import s from './s.module.less';

type Props = {
    open: boolean;
    onClose: () => void;
    onConnected?: (ehrName: string) => void;
    // 断开成功后回调（宿主用来同步勾选态，如 billingEhrs 里移除该 EHR）
    onDisconnected?: (ehrName: string) => void;
};

const EhrConnectModal = ({ open, onClose, onConnected, onDisconnected }: Props) => {
    const [connections, , refreshConnections, connectedNames] = useEhrConnections();
    const [ehrOptions] = useEhrOptions();
    const [flowEhr, setFlowEhr] = useState<string | null>(null);
    const [showOther, setShowOther] = useState(false);
    const [disconnecting, setDisconnecting] = useState<number | null>(null);

    // 按后端返回顺序展示；custom（isCustom，长期 pending 等内部对接）也入列，行尾显示 Setup in progress
    const connectedList = connections.filter((c) => c.connectionStatus === 'connected' || c.isCustom);

    const availableEhrs = useMemo(
        () => ehrOptions.filter((e) => !e.upcoming && !connectedNames.has(e.name)),
        [ehrOptions, connectedNames],
    );
    const upcomingEhrs = useMemo(() => ehrOptions.filter((e) => e.upcoming), [ehrOptions]);

    const handleSelectEhr = (name: string) => {
        if (connectedNames.has(name)) return;
        setFlowEhr(name);
    };

    const handleDisconnect = async (id: number, ehrName: string) => {
        setDisconnecting(id);
        const res = await deleteEhrConnection(id);
        if (res && !res.error) {
            message.success('EHR disconnected');
            refreshConnections();
            onDisconnected?.(ehrName);
        }
        setDisconnecting(null);
    };

    const handleFlowSuccess = () => {
        setFlowEhr(null);
        refreshConnections();
        onConnected?.(flowEhr!);
    };

    const closeOther = () => setShowOther(false);

    const formatDate = (dateStr: string) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return (
        <>
            <Modal
                open={open && !flowEhr}
                onCancel={onClose}
                title={null}
                footer={null}
                width={1000}
                destroyOnClose
                className={s.pickerModal}
            >
                <div className={s.pickerBody}>
                    {!showOther ? (
                        <>
                            <div className={s.pickerHeader}>
                                <h2 className={s.pickerTitle}>Connect an EHR</h2>
                                <p className={s.pickerSubtitle}>Choose the EHR you want Klarity to bill from.</p>
                            </div>

                            <div className={s.pickerScroll}>
                                {connectedList.length > 0 && (
                                    <div className={s.connSection}>
                                        <div className={s.connTitle}>EHR connections</div>
                                        <div className={s.connList}>
                                            {connectedList.map((c) => (
                                                <div key={c.id} className={s.connRow}>
                                                    <div className={s.connLeft}>
                                                        <div className={s.connCheckIcon}>
                                                            <svg
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="#16a34a"
                                                                strokeWidth="2.2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                width="20"
                                                                height="20"
                                                            >
                                                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                                                <path d="m9 11 3 3L22 4" />
                                                            </svg>
                                                        </div>
                                                        <div className={s.connInfo}>
                                                            <div className={s.connNameRow}>
                                                                <span className={s.connName}>{c.ehrSystem}</span>
                                                                {c.isPrimary === 1 && (
                                                                    <span className={s.connPrimaryTag}>Primary</span>
                                                                )}
                                                            </div>
                                                            <div className={s.connDate}>
                                                                Connected since {formatDate(c.createdAt)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className={s.connRight}>
                                                        <span
                                                            className={s.disconnectLink}
                                                            onClick={() =>
                                                                !disconnecting && handleDisconnect(c.id, c.ehrSystem)
                                                            }
                                                        >
                                                            {disconnecting === c.id ? 'Disconnecting...' : 'Disconnect'}
                                                        </span>
                                                        {c.isCustom ? (
                                                            <span
                                                                className={`${s.connStatusText} ${s.connStatusInProgress}`}
                                                            >
                                                                <span className={s.connStatusDot} />
                                                                Setup in progress
                                                            </span>
                                                        ) : (
                                                            <span className={s.connStatusText}>
                                                                <span className={s.connStatusDot} />
                                                                Connected
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div
                                    className={`${s.gridSection} ${connectedList.length > 0 ? s.gridSectionWithConn : ''}`}
                                >
                                    {connectedList.length > 0 && (
                                        <>
                                            <div className={s.gridTitle}>Connect another EHR</div>
                                            <div className={s.gridSub}>
                                                Klarity only bills patients in the EHRs you connect.
                                            </div>
                                        </>
                                    )}
                                    <div className={s.ehrGrid}>
                                        {availableEhrs.map((ehr) => (
                                            <div
                                                key={ehr.name}
                                                className={s.ehrCard}
                                                onClick={() => handleSelectEhr(ehr.name)}
                                            >
                                                <EhrBadge ehrName={ehr.name} />
                                                <span className={s.ehrCardName}>{ehr.name}</span>
                                            </div>
                                        ))}
                                        {upcomingEhrs.map((ehr) => (
                                            <div key={ehr.name} className={s.ehrCardUpcoming}>
                                                <EhrBadge ehrName={ehr.name} />
                                                <div className={s.ehrCardUpcomingText}>
                                                    <span className={s.ehrCardName}>{ehr.name}</span>
                                                    <span className={s.ehrCardSub}>Upcoming</span>
                                                </div>
                                            </div>
                                        ))}
                                        <div className={s.otherCard} onClick={() => setShowOther(true)}>
                                            <div className={s.otherPlusIcon}>
                                                <PlusOutlined />
                                            </div>
                                            <div className={s.ehrCardUpcomingText}>
                                                <span className={s.ehrCardName}>My EHR isn&apos;t listed</span>
                                                <span className={s.ehrCardSub2}>Tell us what you use</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Tell us about your EHR：自建/未收录 EHR 提交（居中标题，左侧返回） */}
                            <div className={s.pickerHeader}>
                                <div className={s.customHeaderRow}>
                                    <span className={s.pickerBackBtn} onClick={closeOther}>
                                        <ArrowLeftOutlined />
                                    </span>
                                    <h2 className={s.customTitle}>Tell us about your EHR</h2>
                                    {/* 占位与返回按钮等宽，保证标题在弹窗内绝对居中 */}
                                    <span className={s.customHeaderSpacer} />
                                </div>
                            </div>
                            <CustomEhrForm
                                onCancel={closeOther}
                                onSubmitted={(name) => {
                                    closeOther();
                                    onConnected?.(name);
                                }}
                            />
                        </>
                    )}
                </div>
            </Modal>

            {flowEhr && (
                <EhrConnectFlow
                    ehrName={flowEhr}
                    open={!!flowEhr}
                    onClose={() => setFlowEhr(null)}
                    onSuccess={handleFlowSuccess}
                />
            )}
        </>
    );
};

export default EhrConnectModal;
